import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import Navbar from '../components/Navbar';
import DisclaimerBanner from '../components/DisclaimerBanner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Settings as SettingsIcon, User, MapPin, Stethoscope, Globe, Loader2, Check, GraduationCap, Briefcase, Shield, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRIES, PROVINCES } from '../data/countries';

const SettingsPage = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [termsContent, setTermsContent] = useState(null);
  const [formData, setFormData] = useState({
    specialty: user?.specialty || '',
    role: user?.role || '',
    profession_type: user?.profession_type || '',
    country: user?.country || '',
    province: user?.province || ''
  });

  const provinces = PROVINCES[formData.country] || PROVINCES.DEFAULT;

  const handleViewTerms = async () => {
    try {
      const response = await api.getTerms();
      setTermsContent(response.data);
      setShowTerms(true);
    } catch (err) {
      toast.error('Failed to load terms');
    }
  };

  const handleSave = async () => {
    if (!formData.specialty || !formData.role || !formData.profession_type || !formData.country) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.updateProfile(formData);
      updateUser(response.data);
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <main className="pt-20 pb-16 main-content">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <SettingsIcon className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
              <p className="text-sm text-slate-500">Manage your account and preferences</p>
            </div>
          </div>

          {/* Profile Card */}
          <Card className="border-slate-200 mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-slate-600" />
                Profile Information
              </CardTitle>
              <CardDescription>Update your professional details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Privacy Notice */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <p className="text-xs text-slate-600 leading-relaxed">
                  <strong>Privacy:</strong> This information is only used to tailor search results and improve context relevance. 
                  All analytics are performed on de-identified, aggregate data only.
                </p>
              </div>

              {/* Specialty - Open Text */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Stethoscope className="h-4 w-4 text-slate-500" />
                  Specialty / Area of Practice <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.specialty}
                  onChange={(e) => setFormData(prev => ({ ...prev, specialty: e.target.value }))}
                  placeholder="e.g., Infectious Disease, Emergency Medicine, Nursing..."
                  required
                  data-testid="settings-specialty"
                />
              </div>

              {/* Role */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-slate-500" />
                  Role <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, role: v }))}
                >
                  <SelectTrigger data-testid="settings-role">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trainee">Trainee (Student, Resident, Fellow)</SelectItem>
                    <SelectItem value="faculty_staff">Faculty / Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Profession Type */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-slate-500" />
                  Profession Type <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={formData.profession_type} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, profession_type: v }))}
                >
                  <SelectTrigger data-testid="settings-profession-type">
                    <SelectValue placeholder="Select profession type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="medical_doctor">Medical Doctor (MD, DO)</SelectItem>
                    <SelectItem value="allied_health">Allied Health Professional</SelectItem>
                    <SelectItem value="non_md_researcher">Non-MD Researcher</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Country */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-slate-500" />
                  Country <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={formData.country} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, country: v, province: '' }))}
                >
                  <SelectTrigger data-testid="settings-country">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Province/State */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-slate-500" />
                  Province / State <span className="text-slate-400 text-xs font-normal">(optional)</span>
                </Label>
                <Select 
                  value={formData.province} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, province: v }))}
                  disabled={!formData.country}
                >
                  <SelectTrigger data-testid="settings-province">
                    <SelectValue placeholder="Select region (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {provinces.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleSave}
                disabled={loading}
                className="w-full h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-medium mt-4"
                data-testid="settings-save"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Donation Status Card */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Supporter Status</CardTitle>
              <CardDescription>Your contribution status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-slate-50 rounded-lg">
                {user?.is_ambassador ? (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-100 to-sky-100 flex items-center justify-center">
                      <Check className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">NoSpin Ambassador</p>
                      <p className="text-sm text-slate-500">Lifetime supporter - thank you!</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Free User</p>
                    <p className="text-sm text-slate-500 mb-3">
                      You have 5 free searches per day. Donations help keep NoSpin independent.
                    </p>
                    <p className="text-xs text-slate-400">
                      When you exceed your daily limit, you'll be prompted to donate (optional).
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Terms & Privacy Card */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Legal
              </CardTitle>
              <CardDescription>Terms of Use and Privacy Policy</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                onClick={handleViewTerms}
                className="w-full justify-start"
                data-testid="view-terms-settings"
              >
                <Shield className="h-4 w-4 mr-2" />
                View Terms of Use & Privacy Policy
              </Button>
              {user?.terms_accepted && (
                <p className="text-xs text-slate-400 mt-2">
                  Accepted on {user.terms_accepted_at ? new Date(user.terms_accepted_at).toLocaleDateString() : 'sign-up'}
                  {user.terms_accepted_via === 'google_signin' && ' via Google Sign-In'}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Terms Dialog */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                <Shield className="h-5 w-5 text-sky-600" />
              </div>
              <div>
                <DialogTitle className="text-xl">Terms of Use</DialogTitle>
                <p className="text-sm text-slate-500">Version {termsContent?.version} • Effective {termsContent?.effective_date}</p>
              </div>
            </div>
          </DialogHeader>
          <ScrollArea className="h-[400px] rounded-lg border border-slate-200 p-4">
            <div className="prose prose-sm prose-slate max-w-none">
              <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">
                {termsContent?.content}
              </pre>
            </div>
          </ScrollArea>
          <div className="flex justify-end mt-4">
            <Button
              variant="outline"
              onClick={() => setShowTerms(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DisclaimerBanner />
    </div>
  );
};

export default SettingsPage;
